/**
 * AI Copilot panel: a dismissible modal (reused from the multiplayer lobby
 * and the in-game HUD) that shows the player their seat's MCP endpoint +
 * bearer token, a ready-to-paste `claude mcp add` command, and a selector
 * for the copilot's permission clearance ({@link CopilotMode}).
 *
 * The panel never persists anything itself — the token/endpoint are only
 * ever read live off the GameSession (which gets them from the room's
 * per-recipient 'lobby'/'start' server messages), and the clearance is just
 * sent to the room on change (default 'economy', mirroring the server).
 *
 * Instantiate once per menu render / per game (its root element is a child
 * of the given `root`, so it's cleaned up automatically the next time that
 * root's innerHTML is rewritten — see menu.ts / hud.ts for the two call
 * sites).
 */

import type { CopilotMode } from '../sim';
import type { GameSession } from './session';

const MODE_ORDER: CopilotMode[] = ['advise', 'economy', 'full'];

const MODE_INFO: Record<CopilotMode, { label: string; desc: string }> = {
  advise: { label: 'Advise', desc: 'Observe your economy and message you — no actions.' },
  economy: { label: 'Economy', desc: 'Also manage FinOps: research, build, train, fix anomalies, advance phase.' },
  full: { label: 'Full', desc: 'Also command combat: attack and move units.' },
};

const TOKEN_MASK = '•'.repeat(24);

export class CopilotPanel {
  private el: HTMLElement;
  private mode: CopilotMode = 'economy';
  private lastToken: string | null = null;
  private lastSession: GameSession | null = null;

  constructor(
    root: HTMLElement,
    private getSession: () => GameSession | null,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'copilot-overlay';
    this.el.hidden = true;
    root.appendChild(this.el);
  }

  open(): void {
    this.lastToken = null; // force a fresh render (re-masks the token, etc.)
    this.lastSession = null;
    this.render();
    this.el.hidden = false;
  }

  close(): void {
    this.el.hidden = true;
  }

  /** Call once per frame (or per lobby update) while the panel might be
   *  open — cheaply re-renders only when the token/session actually changed,
   *  so it doesn't clobber in-progress interactions (revealed token, a
   *  "Copied!" button label) with a redundant rebuild. */
  update(): void {
    if (this.el.hidden) return;
    const session = this.getSession();
    const token = session?.copilotToken ?? null;
    if (token !== this.lastToken || session !== this.lastSession) {
      this.lastToken = token;
      this.lastSession = session;
      this.render();
    }
  }

  private render(): void {
    const session = this.getSession();
    const isMultiplayer = session?.mode === 'multiplayer';
    const roomCode = session?.roomCode ?? null;
    const token = session?.copilotToken ?? null;
    const endpoint = isMultiplayer && roomCode ? `${window.location.origin}/api/room/${roomCode}/mcp` : null;

    let tokenSectionHtml: string;
    if (!isMultiplayer) {
      tokenSectionHtml = `<p class="copilot-note">Multiplayer rooms only — start or join a multiplayer room to get your seat's AI Copilot endpoint and token.</p>`;
    } else if (!token || !endpoint) {
      tokenSectionHtml = `<p class="copilot-note">Waiting for your copilot token from the server&hellip;</p>`;
    } else {
      const claudeCmd = `claude mcp add --transport http tokenomics ${endpoint} --header "Authorization: Bearer ${token}"`;
      tokenSectionHtml = `
        <div class="copilot-field">
          <label>MCP endpoint</label>
          <div class="copilot-copyrow">
            <code class="copilot-mono">${escapeHtml(endpoint)}</code>
            <button class="btn-small copilot-copy-btn" type="button" data-copy="${escapeAttr(endpoint)}">Copy</button>
          </div>
        </div>
        <div class="copilot-field">
          <label>Bearer token <span class="copilot-hint">(tap to reveal)</span></label>
          <div class="copilot-copyrow">
            <code class="copilot-mono copilot-token-masked" id="copilot-token" data-token="${escapeAttr(token)}" title="Click to reveal">${TOKEN_MASK}</code>
            <button class="btn-small copilot-copy-btn" type="button" data-copy="${escapeAttr(token)}">Copy</button>
          </div>
        </div>
        <div class="copilot-field">
          <label>Claude Code setup</label>
          <div class="copilot-copyrow">
            <code class="copilot-mono copilot-cmd">${escapeHtml(claudeCmd)}</code>
            <button class="btn-small copilot-copy-btn" type="button" data-copy="${escapeAttr(claudeCmd)}">Copy</button>
          </div>
        </div>
      `;
    }

    const modeOptionsHtml = MODE_ORDER.map((m) => {
      const info = MODE_INFO[m];
      const disabled = !isMultiplayer;
      return `
        <label class="copilot-mode-option ${m === this.mode ? 'selected' : ''} ${disabled ? 'disabled' : ''}">
          <input type="radio" name="copilot-mode" value="${m}" ${m === this.mode ? 'checked' : ''} ${disabled ? 'disabled' : ''} />
          <span class="copilot-mode-label">${info.label}</span>
          <span class="copilot-mode-desc">${info.desc}</span>
        </label>`;
    }).join('');

    this.el.innerHTML = `
      <div class="copilot-box">
        <div class="copilot-head">
          <h2>&#129302; AI Copilot</h2>
          <button class="advisor-close" id="copilot-close" aria-label="Close" title="Close">&#10005;</button>
        </div>
        <p class="copilot-intro">Your own AI plays FinOps for you — connect an MCP client (Claude Code, claude.ai,
          or your own script) to your seat and it can watch your economy, advise you, and, if you allow it, act on
          your behalf live in the match.</p>
        ${tokenSectionHtml}
        <div class="copilot-field">
          <label>Copilot clearance</label>
          <div class="copilot-mode-options">${modeOptionsHtml}</div>
        </div>
        <div class="help-close-row">
          <button class="btn-hud" id="copilot-close-2">Close</button>
        </div>
      </div>
    `;

    this.el.querySelector('#copilot-close')!.addEventListener('click', () => this.close());
    this.el.querySelector('#copilot-close-2')!.addEventListener('click', () => this.close());
    this.el.addEventListener('click', (ev) => {
      if (ev.target === this.el) this.close(); // click on the scrim, outside the box
    });

    const tokenEl = this.el.querySelector<HTMLElement>('#copilot-token');
    tokenEl?.addEventListener('click', () => {
      const revealed = tokenEl.classList.toggle('revealed');
      tokenEl.textContent = revealed ? (tokenEl.dataset.token ?? '') : TOKEN_MASK;
    });

    this.el.querySelectorAll<HTMLButtonElement>('.copilot-copy-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        copyToClipboard(btn.dataset.copy ?? '');
        const original = btn.textContent;
        btn.textContent = 'Copied!';
        window.setTimeout(() => {
          btn.textContent = original;
        }, 1200);
      });
    });

    this.el.querySelectorAll<HTMLInputElement>('input[name="copilot-mode"]').forEach((input) => {
      input.addEventListener('change', () => {
        this.mode = input.value as CopilotMode;
        this.getSession()?.setCopilotMode(this.mode);
        this.render();
      });
    });
  }
}

function copyToClipboard(text: string): void {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
  } else {
    fallbackCopy(text);
  }
}

function fallbackCopy(text: string): void {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  try {
    document.execCommand('copy');
  } catch {
    // clipboard unavailable — the raw text is still visible/selectable in the field.
  }
  ta.remove();
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeAttr(str: string): string {
  return escapeHtml(str);
}
